/**
 * PATH: packages/ui/src/components/email/chipIdentity.ts
 * FILE_ID: UI-EMAIL-CHIPIDENTITY-001
 * PURPOSE: Pure, display-only formatter for a PERSON/MAILBOX identity shown on
 *          a compact email chip. Turns { name, email } into a short, scannable
 *          label (first name → email local-part → "Unknown") plus the full
 *          address for the chip's `title`/aria tooltip. The visible chip never
 *          has to spell out "abhishek@finsoeasy.com" — it reads "abhishek" (or
 *          "Jake") and the full address stays one hover away.
 * WHY:     Operator (2026-06-21, with screenshot): an email-attribution chip
 *          rendering the WHOLE address ("abhishek@finsoeasy.com") was a width
 *          hog that made the card "really hard to action" and hard to read.
 *          The mailbox pill already shortened inline (3 hand-rolled copies);
 *          this lifts that logic into ONE tested helper so every chip that
 *          shows a person/mailbox shortens identically, with no drift and the
 *          full address preserved in the tooltip.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌────────────────────┐
 *   │ { name,email}│ → │ first name → local-  │ → │ { label, title }    │
 *   │              │   │ part → "Unknown"     │   │ (full addr in title)│
 *   └──────────────┘   └──────────────────────┘   └────────────────────┘
 */

export interface ChipIdentityInput {
  /** Friendly name (e.g. "Jake Smith"). The first whitespace token becomes the
   *  label so the chip reads "Jake", not "Jake Smith". */
  name?: string | null
  /** Email address. Its local-part (before '@') is the label fallback when no
   *  name is present, and the WHOLE address is always the tooltip `title`. */
  email?: string | null
}

export interface ChipIdentity {
  /** The short, visible chip text: first name → local-part → "Unknown". */
  label: string
  /** The full-fidelity tooltip: the whole email when present, else the name,
   *  else "Unknown". Callers may override with a more specific phrase. */
  title: string
}

/**
 * formatChipIdentity({ name, email }) → { label, title }. Pure I→O.
 *
 * Label preference: first token of `name` → local-part of `email` → "Unknown".
 * Title is always the full address (so the chip is short but loses nothing).
 *
 * @example formatChipIdentity({ name: 'Jake Smith', email: 'jake@x.com' })
 *          → { label: 'Jake', title: 'jake@x.com' }
 * @example formatChipIdentity({ email: 'abhishek@finsoeasy.com' })
 *          → { label: 'abhishek', title: 'abhishek@finsoeasy.com' }
 */
export function formatChipIdentity(input: ChipIdentityInput): ChipIdentity {
  const name = (input.name ?? '').trim()
  const email = (input.email ?? '').trim()

  // First whitespace token of the name (so "Jake Smith" → "Jake").
  const firstName = name ? (name.split(/\s+/)[0] ?? '') : ''
  // Local-part of the address (so "abhishek@finsoeasy.com" → "abhishek").
  // A leading-'@' / empty local-part yields '' and falls through to the name
  // or the "Unknown" guard rather than printing a bare "@domain".
  const localPart = email ? (email.split('@')[0] ?? '') : ''

  const label = firstName || localPart || 'Unknown'
  const title = email || name || 'Unknown'
  return { label, title }
}
